<template>
    <div class="cib-container">
        <div class="cib-header">
            <strong class="cib-title">Script Editor</strong>
            <div class="cib-header-right">
                <button class="cib-icon-btn" @click="showEditorMenu=!showEditorMenu" :aria-expanded="showEditorMenu" aria-label="Script options" title="Script options">⋯</button>
                <button class="cib-icon-btn" @click="toggleExpand" aria-label="Toggle editor layout" title="Toggle editor layout">⛶</button>
                <button class="cib-icon-btn" @click="$emit('close')" aria-label="Close Script Editor" title="Close">×</button>
            </div>
        </div>
        <div v-if="showEditorMenu" class="cib-editor-menu" aria-label="Script options">
            <button @click="showTemplates=!showTemplates; showEditorMenu=false">Templates</button>
            <button @click="showSaved=!showSaved; showEditorMenu=false">Saved scripts</button>
            <button @click="toggleAiPanel(); showEditorMenu=false">AI generation</button>
            <button @click="tab='inputs'; showEditorMenu=false">Inputs</button>
            <button @click="tab='style'; showEditorMenu=false">Style &amp; outputs</button>
            <button @click="tab='code'; showEditorMenu=false">Code</button>
        </div>
        <!-- AI Generation Panel -->
        <div v-if="showAiPanel" class="cib-ai-panel">
            <div class="cib-ai-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="11" width="18" height="10" rx="2"/>
                    <circle cx="12" cy="5" r="2"/>
                    <path d="M12 7v4"/>
                    <rect x="8" y="15" width="2" height="2" rx="0.5"/>
                    <rect x="14" y="15" width="2" height="2" rx="0.5"/>
                    <path d="M8 7l-2-2"/>
                    <path d="M16 7l2-2"/>
                    <path d="M9 6l1-3"/>
                    <path d="M15 6l-1-3"/>
                </svg>
                <span>AI Indicator Generator</span>
                <span v-if="aiCredits !== null" class="cib-ai-credits" :class="{ low: aiCredits <= 5 }">
                    {{ aiCredits }} credit{{ aiCredits !== 1 ? 's' : '' }}
                </span>
            </div>
            <div class="cib-ai-body">
                <textarea
                    ref="aiPrompt"
                    class="cib-ai-prompt"
                    v-model="aiPromptText"
                    placeholder="Describe the indicator you want to create..."
                    @keydown.ctrl.enter="generateFromAI"
                    @keydown.meta.enter="generateFromAI"
                ></textarea>
                <!-- Formatted example suggestions -->
                <div v-if="!aiPromptText.trim()" class="cib-ai-examples">
                    <div class="cib-ai-examples-title">Try these examples:</div>
                    <div class="cib-ai-examples-grid">
                        <button class="cib-ai-example-chip" @click="aiPromptText = 'RSI with dynamic overbought/oversold levels and color-coded background'">
                            <span class="cib-ai-chip-icon cib-chip-oscillator">📊</span>
                            <span class="cib-ai-chip-text">RSI with dynamic levels</span>
                        </button>
                        <button class="cib-ai-example-chip" @click="aiPromptText = 'EMA crossover with buy/sell arrows on chart'">
                            <span class="cib-ai-chip-icon cib-chip-signal">📈</span>
                            <span class="cib-ai-chip-text">EMA crossover signals</span>
                        </button>
                        <button class="cib-ai-example-chip" @click="aiPromptText = 'Bollinger Bands with filled area and squeeze detection'">
                            <span class="cib-ai-chip-icon cib-chip-volatility">🎯</span>
                            <span class="cib-ai-chip-text">Bollinger Bands + squeeze</span>
                        </button>
                        <button class="cib-ai-example-chip" @click="aiPromptText = 'SuperTrend indicator with trend-colored line and direction arrows'">
                            <span class="cib-ai-chip-icon cib-chip-trend">🔀</span>
                            <span class="cib-ai-chip-text">SuperTrend with arrows</span>
                        </button>
                        <button class="cib-ai-example-chip" @click="aiPromptText = 'Heikin-Ashi candles overlay on chart'">
                            <span class="cib-ai-chip-icon cib-chip-candle">🕯️</span>
                            <span class="cib-ai-chip-text">Heikin-Ashi candles</span>
                        </button>
                        <button class="cib-ai-example-chip" @click="aiPromptText = 'Support and resistance levels with horizontal lines and price labels'">
                            <span class="cib-ai-chip-icon cib-chip-drawing">📐</span>
                            <span class="cib-ai-chip-text">Support/Resistance lines</span>
                        </button>
                        <button class="cib-ai-example-chip" @click="aiPromptText = 'Volume profile histogram with high volume bar highlighting'">
                            <span class="cib-ai-chip-icon cib-chip-volume">📊</span>
                            <span class="cib-ai-chip-text">Volume histogram</span>
                        </button>
                        <button class="cib-ai-example-chip" @click="aiPromptText = 'MACD with histogram, signal line, and crossover markers'">
                            <span class="cib-ai-chip-icon cib-chip-oscillator">⚡</span>
                            <span class="cib-ai-chip-text">MACD + crossover markers</span>
                        </button>
                    </div>
                </div>
                <div class="cib-ai-footer">
                    <span class="cib-ai-hint">Ctrl+Enter to generate</span>
                    <button
                        class="cib-btn cib-btn-ai"
                        @click="generateFromAI"
                        :disabled="aiGenerating || !aiPromptText.trim()"
                    >
                        <span v-if="aiGenerating" class="cib-spinner"></span>
                        <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                        </svg>
                        {{ aiGenerating ? 'Generating...' : 'Generate' }}
                    </button>
                </div>
                <div v-if="aiError" class="cib-ai-error">{{ aiError }}</div>
                <div v-if="aiDescription" class="cib-ai-desc">{{ aiDescription }}</div>
            </div>
        </div>

        <!-- Templates dropdown -->
        <div v-if="showTemplates" class="cib-dropdown">
            <div class="cib-dropdown-title">Templates</div>
            <div
                v-for="tmpl in templates"
                :key="tmpl.id"
                class="cib-dropdown-item"
                @click="loadTemplate(tmpl)"
            >
                <span class="cib-tmpl-dot" :class="'cib-cat-' + tmpl.category"></span>
                <span>{{ tmpl.name }}</span>
                <span class="cib-tmpl-side">{{ tmpl.side === 'onchart' ? 'overlay' : 'pane' }}</span>
            </div>
        </div>

        <!-- Saved dropdown -->
        <div v-if="showSaved" class="cib-dropdown">
            <div class="cib-dropdown-title">Saved Indicators</div>
            <div v-if="!savedList.length" class="cib-dropdown-empty">No saved indicators</div>
            <div
                v-for="ind in savedList"
                :key="ind.id"
                class="cib-dropdown-item"
                @click="loadSaved(ind)"
            >
                <span>{{ ind.name }}</span>
                <button class="cib-dropdown-del" @click.stop="deleteSaved(ind.id)">×</button>
            </div>
        </div>

        <div class="cib-script-toolbar">
            <input class="cib-name-input" v-model="indicator.name" placeholder="Untitled script" aria-label="Script name" />
            <button class="cib-icon-btn" @click="runIndicator" :disabled="isRunning" aria-label="Run script" title="Run script">▷</button>
            <button class="cib-icon-btn" @click="saveIndicator" aria-label="Save script" title="Save script">↑</button>
            <button class="cib-btn" @click="applyToChart" :disabled="!lastResult" title="Add script to chart">Add</button>
        </div>
        <div v-if="tab!=='code'" class="cib-tabs"><button class="cib-tab" @click="tab='code'">← Code</button><strong>{{ tab==='inputs'?'Inputs':'Style & outputs' }}</strong><select v-model="indicator.side" aria-label="Script placement"><option value="onchart">Overlay</option><option value="offchart">Pane</option></select></div>
        <!-- Code Tab -->
        <div v-show="tab === 'code'" class="cib-tab-body cib-code-tab">
            <div class="cib-editor-wrap" ref="cibEditorWrap"></div>
        </div>

        <!-- Inputs Tab -->
        <div v-show="tab === 'inputs'" class="cib-tab-body cib-inputs-tab">
            <div v-if="!indicator.options.length" class="cib-empty">
                No inputs defined. Add parameters in your script via <code>options.key</code>.
            </div>
            <div v-for="(opt, i) in indicator.options" :key="i" class="cib-input-row">
                <div class="cib-input-header">
                    <input class="cib-input-key" v-model="opt.key" placeholder="key" @input="syncDefaults" />
                    <select class="cib-input-type" v-model="opt.type" @change="syncDefaults">
                        <option value="number">Number</option>
                        <option value="select">Select</option>
                        <option value="boolean">Boolean</option>
                    </select>
                    <button class="cib-input-del" @click="removeInput(i)">×</button>
                </div>
                <div class="cib-input-body">
                    <template v-if="opt.type === 'number'">
                        <div class="cib-input-num-row">
                            <label>Val</label>
                            <input type="number" v-model.number="currentValues[opt.key]" @input="debouncedRun" />
                            <label>Min</label>
                            <input type="number" v-model.number="opt.min" />
                            <label>Max</label>
                            <input type="number" v-model.number="opt.max" />
                        </div>
                        <input type="range" class="cib-range"
                            :value="currentValues[opt.key]"
                            :min="opt.min || 1" :max="opt.max || 200" :step="opt.step || 1"
                            @input="currentValues[opt.key] = Number($event.target.value); debouncedRun()"
                        />
                    </template>
                    <template v-if="opt.type === 'select'">
                        <input class="cib-choices-input" :value="(opt.choices||[]).join(', ')"
                            @input="opt.choices = $event.target.value.split(',').map(s=>s.trim()).filter(Boolean); syncDefaults()"
                            placeholder="open, high, low, close" />
                        <select :value="currentValues[opt.key]" @change="currentValues[opt.key]=$event.target.value; debouncedRun()">
                            <option v-for="c in (opt.choices||[])" :key="c" :value="c">{{ c }}</option>
                        </select>
                    </template>
                    <template v-if="opt.type === 'boolean'">
                        <label class="cib-toggle">
                            <input type="checkbox" :checked="currentValues[opt.key]"
                                @change="currentValues[opt.key]=$event.target.checked; debouncedRun()" />
                            <span class="cib-toggle-track"></span>
                        </label>
                    </template>
                </div>
            </div>
            <button class="cib-add-input-btn" @click="addInput">+ Add Input</button>
        </div>

        <!-- Style Tab -->
        <div v-show="tab === 'style'" class="cib-tab-body cib-style-tab">
            <div v-for="(out, i) in indicator.plotConfig.outputs" :key="i" class="cib-style-row">
                <input class="cib-style-key" v-model="out.key" placeholder="key" />
                <input class="cib-style-label" v-model="out.label" placeholder="Label" />
                <input type="color" v-model="out.color" class="cib-color-pick" />
                <select v-model="out.style" class="cib-style-sel">
                    <option value="line">Line</option>
                    <option value="dashed">Dashed</option>
                    <option value="histogram">Histogram</option>
                </select>
                <button class="cib-input-del" @click="indicator.plotConfig.outputs.splice(i,1)">×</button>
            </div>
            <button class="cib-add-input-btn" @click="addOutput">+ Add Output</button>
        </div>

        <!-- Status + Actions -->
        <div class="cib-footer">
            <div class="cib-status">
                <span v-if="errors.length" class="cib-status-err">{{ errors.length }} error{{ errors.length>1?'s':'' }}</span>
                <span v-else-if="lastElapsed !== null" class="cib-status-ok">✓ {{ lastElapsed }}ms</span>
                <span v-if="appliedToChart" class="cib-status-applied">On chart</span>
            </div>
            <small>CandleScript · JavaScript</small>
            <button v-if="appliedToChart" class="cib-btn" @click="removeFromChart">Remove from chart</button>
        </div>

        <!-- Errors -->
        <div v-if="errors.length" class="cib-errors">
            <div v-for="(e, i) in errors" :key="i" class="cib-error-line">{{ e.message }}</div>
        </div>
    </div>
</template>

<script>
import CodeMirror from 'codemirror';
import { markRaw } from 'vue';
import 'codemirror/mode/javascript/javascript.js';
import 'codemirror/addon/hint/show-hint.js';
import 'codemirror/addon/hint/javascript-hint.js';
import 'codemirror/addon/edit/closebrackets.js';
import 'codemirror/addon/edit/matchbrackets.js';

import { TEMPLATE_LIST } from '../../candle-script/templates/index.js';
import { createIndicatorConfig, getDefaultValues } from '../../candle-script/schema.js';
import { CandleScriptExecutor } from '../../candle-script/engine/executor.js';
import { validateCode } from '../../candle-script/engine/static-analyzer.js';
import { saveIndicator, loadAllIndicators, deleteIndicator } from '../../candle-script/storage.js';
import {customSignalData} from '../../workspace/custom-indicator-signals.mjs';
import { getRunner } from '../../candle-script/runner.js';
import aiIndicatorService from '../../services/ai_indicator_service.js';
import AIChatService from '../../services/ai_chat_service.js';

const AC_ITEMS = [
    { name: 'sma', type: 'function', desc: 'Simple MA', insert: 'sma(close, length)' },
    { name: 'ema', type: 'function', desc: 'Exponential MA', insert: 'ema(close, length)' },
    { name: 'rsi', type: 'function', desc: 'RSI', insert: 'rsi(close, length)' },
    { name: 'atr', type: 'function', desc: 'ATR', insert: 'atr(high, low, close, length)' },
    { name: 'highest', type: 'function', desc: 'Highest', insert: 'highest(arr, length)' },
    { name: 'lowest', type: 'function', desc: 'Lowest', insert: 'lowest(arr, length)' },
    { name: 'sum', type: 'function', desc: 'Rolling sum', insert: 'sum(arr, length)' },
    { name: 'stddev', type: 'function', desc: 'Std dev', insert: 'stddev(arr, length)' },
    { name: 'crossover', type: 'function', desc: 'Cross above', insert: 'crossover(a, b)' },
    { name: 'crossunder', type: 'function', desc: 'Cross below', insert: 'crossunder(a, b)' },
    { name: 'bbands', type: 'function', desc: 'Bollinger', insert: 'bbands(close, length, mult)' },
    { name: 'macd', type: 'function', desc: 'MACD', insert: 'macd(close, fast, slow, signal)' },
    { name: 'close', type: 'property', desc: 'Close prices', insert: 'close' },
    { name: 'open', type: 'property', desc: 'Open prices', insert: 'open' },
    { name: 'high', type: 'property', desc: 'High prices', insert: 'high' },
    { name: 'low', type: 'property', desc: 'Low prices', insert: 'low' },
    { name: 'volume', type: 'property', desc: 'Volume', insert: 'volume' },
    { name: 'wma', type: 'function', desc: 'Weighted MA', insert: 'wma(close, length)' },
    { name: 'vwap', type: 'function', desc: 'VWAP', insert: 'vwap(high, low, close, volume)' },
    { name: 'stoch', type: 'function', desc: 'Stochastic', insert: 'stoch(high, low, close, kLen, dLen)' },
    { name: 'input', type: 'property', desc: 'OHLCV data', insert: 'input' },
    { name: 'options', type: 'property', desc: 'User options', insert: 'options' },
    { name: 'Math.abs', type: 'function', desc: 'Absolute', insert: 'Math.abs(x)' },
    { name: 'Math.max', type: 'function', desc: 'Maximum', insert: 'Math.max(a, b)' },
    { name: 'Math.min', type: 'function', desc: 'Minimum', insert: 'Math.min(a, b)' },
    { name: 'isNaN', type: 'function', desc: 'Check NaN', insert: 'isNaN(x)' },
    { name: 'Float64Array', type: 'class', desc: 'Typed array', insert: 'Float64Array' },
    // Drawing API
    { name: 'plot.line', type: 'function', desc: 'Draw line series', insert: 'plot.line(values, { color: \'#2962ff\', width: 1.5, style: \'solid\', title: \'\' })' },
    { name: 'plot.hline', type: 'function', desc: 'Horizontal line', insert: 'plot.hline(price, { color: \'#787b86\', style: \'dashed\', title: \'\' })' },
    { name: 'plot.shape', type: 'function', desc: 'Plot markers', insert: 'plot.shape(series, { shape: \'circle\', color: \'#2962ff\', location: \'absolute\' })' },
    { name: 'plot.arrow', type: 'function', desc: 'Plot arrows', insert: 'plot.arrow(series, { direction: \'up\', color: \'#26a69a\' })' },
    { name: 'plot.text', type: 'function', desc: 'Plot text label', insert: 'plot.text(barIndex, price, \'text\', { color: \'#131722\', size: 12 })' },
    { name: 'plot.candle', type: 'function', desc: 'Custom candles', insert: 'plot.candle({ open, high, low, close }, { colorUp: \'#26a69a\', colorDown: \'#ef5350\' })' },
    { name: 'plot.fill', type: 'function', desc: 'Fill between lines', insert: 'plot.fill(series1, series2, { color: \'rgba(41,98,255,0.1)\' })' },
    { name: 'plot.bgcolor', type: 'function', desc: 'Background color', insert: 'plot.bgcolor(colors)' },
    { name: 'plot.histogram', type: 'function', desc: 'Histogram bars', insert: 'plot.histogram(values, { colorUp: \'#26a69a\', colorDown: \'#ef5350\' })' },
];

/**
 * Custom hint function for CandleScript autocomplete in the inline editor
 */
function cibHintFunction(editor) {
    const cursor = editor.getCursor();
    const token = editor.getTokenAt(cursor);
    let start = token.start;
    let word = token.string;

    if (/[^\w.]/.test(word)) {
        word = '';
        start = cursor.ch;
    }

    const prefix = word.toLowerCase();
    if (prefix.length < 1) return null;

    const matches = AC_ITEMS.filter(s =>
        s.name.toLowerCase().startsWith(prefix)
    ).map(s => ({
        text: s.insert,
        displayText: s.name + ' — ' + s.desc,
        className: 'cib-hint-' + s.type,
        render: function(el, self, data) {
            const wrapper = document.createElement('div');
            wrapper.className = 'cib-hint-item';

            const icon = document.createElement('span');
            icon.className = 'cib-hint-icon cib-hint-' + s.type;
            icon.textContent = s.type === 'function' ? 'ƒ' : s.type === 'class' ? '●' : '◆';
            wrapper.appendChild(icon);

            const name = document.createElement('span');
            name.className = 'cib-hint-name';
            name.textContent = s.name;
            wrapper.appendChild(name);

            const desc = document.createElement('span');
            desc.className = 'cib-hint-desc';
            desc.textContent = s.desc;
            wrapper.appendChild(desc);

            el.appendChild(wrapper);
        },
    }));

    if (!matches.length) return null;

    return {
        list: matches.slice(0, 8),
        from: CodeMirror.Pos(cursor.line, start),
        to: CodeMirror.Pos(cursor.line, cursor.ch),
    };
}

export default {
    name: 'ChartIndicatorBuilder',
    props: {
        /** The DataCube instance from the chart */
        dc: { type: Object, default: null },
        isDark: { type: Boolean, default: false },
        userId: { type: String, default: null },
    },
    data() {
        const tmpl = TEMPLATE_LIST.find(t => t.id === 'blank');
        const config = createIndicatorConfig({
            name: '',
            code: '',
            options: JSON.parse(JSON.stringify(tmpl.options)),
            plotConfig: JSON.parse(JSON.stringify(tmpl.plotConfig)),
            side: tmpl.side,
        });
        return {
            indicator: config,
            currentValues: getDefaultValues(tmpl.options),
            tab: 'code',
            executor: null,
            isRunning: false,
            lastResult: null,
            lastElapsed: null,
            errors: [],
            appliedToChart: false,
            appliedOverlayId: null, // track which overlay we injected
            templates: TEMPLATE_LIST,
            savedList: [],
            showTemplates: false,
            showEditorMenu: false,
            showSaved: false,
            // CodeMirror editor instance
            cmEditor: null,
            cmIgnoreChange: false,
            _runTimer: null,
            // AI generation
            showAiPanel: false,
            aiPromptText: '',
            aiGenerating: false,
            aiError: '',
            aiDescription: '',
            aiCredits: null,
            isExpanded: false,
        };
    },
    computed: {
        lineCount() {
            return (this.indicator.code || '').split('\n').length;
        },
        aiSettingsEnabled() {
            return AIChatService.isEnabled();
        },
    },
    watch: {
        tab(value) { if(value==='code')this.$nextTick(()=>this.cmEditor?.refresh()); },
        isDark(newVal) {
            if (this.cmEditor) {
                this.cmEditor.setOption('theme', newVal ? 'dracula' : 'default');
            }
        },
        dc(newDc, oldDc) {
            // When DataCube changes (timeframe switch), re-apply indicator
            if (newDc && newDc !== oldDc && this.appliedToChart && this.indicator.code) {
                // Reset applied state since old DataCube is gone
                this.appliedToChart = false;
                this.appliedOverlayId = null;
                // Re-run indicator on new data and re-apply
                // Use a short delay to ensure chart data is populated
                this.$nextTick(() => {
                    const tryReapply = () => {
                        const hasData = newDc.data && newDc.data.chart &&
                            newDc.data.chart.data && newDc.data.chart.data.length > 0;
                        if (hasData) {
                            // Remove any stale empty-data overlays restored by _savedDrawings
                            this.removeStaleOverlays(newDc);
                            this.runIndicator().then(() => {
                                if (this.lastResult && !this.appliedToChart) {
                                    this.applyToChart();
                                }
                            });
                        } else {
                            // Data not yet loaded, retry after a short delay
                            setTimeout(tryReapply, 500);
                        }
                    };
                    tryReapply();
                });
            }
        },
    },
    async created() {
        this.executor = new CandleScriptExecutor();
        try { await this.executor.init(); } catch (e) { /* fallback handled inside */ }
        this.savedList = loadAllIndicators();
        // Load AI credits
        this.loadAiCredits();
        // Sync state with the persistent runner (indicator may already be applied)
        const runner = getRunner();
        const applied = runner.getApplied();
        if (applied.length > 0) {
            const match = applied.find(a => a.id === this.indicator.id || a.name === this.indicator.name);
            if (match) {
                this.appliedToChart = true;
                // Sync the current values from the applied indicator
                if (match.currentValues) {
                    this.currentValues = { ...match.currentValues };
                }
            }
        }
    },
    mounted() {
        this.initCodeMirror();
    },
    beforeDestroy() {
        if (this.executor) this.executor.destroy();
        if (this._runTimer) clearTimeout(this._runTimer);
        if (this.cmEditor) {
            if (typeof this.cmEditor.toTextArea === 'function') this.cmEditor.toTextArea();
            else this.cmEditor.getWrapperElement()?.remove();
            this.cmEditor = null;
        }
    },
    methods: {
        toggleExpand() {
            this.isExpanded = !this.isExpanded;
            this.$emit('toggle-float-mode');
        },
        // ── CodeMirror initialization ──
        initCodeMirror() {
            const container = this.$refs.cibEditorWrap;
            if (!container) return;

            this.cmEditor = markRaw(CodeMirror(container, {
                value: this.indicator.code || '',
                mode: 'javascript',
                theme: this.isDark ? 'dracula' : 'default',
                readOnly: false,
                lineNumbers: true,
                lineWrapping: false,
                matchBrackets: true,
                autoCloseBrackets: true,
                indentUnit: 4,
                tabSize: 4,
                indentWithTabs: false,
                smartIndent: true,
                electricChars: true,
                extraKeys: {
                    'Ctrl-Space': 'autocomplete',
                    'Tab': (cm) => {
                        if (cm.somethingSelected()) {
                            cm.indentSelection('add');
                        } else {
                            cm.replaceSelection('    ', 'end');
                        }
                    },
                    'Shift-Tab': (cm) => {
                        cm.indentSelection('subtract');
                    },
                },
                hintOptions: {
                    hint: cibHintFunction,
                    completeSingle: false,
                    closeOnUnfocus: true,
                    alignWithWord: true,
                },
            }));

            // Listen for changes
            this.cmEditor.on('change', (cm, change) => {
                if (change.origin === 'setValue') return;
                if (this.cmIgnoreChange) return;
                this.indicator.code = cm.getValue();
                this.debouncedRun();
            });

            // Auto-show suggestions as user types
            this.cmEditor.on('inputRead', (cm, change) => {
                if (change.origin === '+input') {
                    const cursor = cm.getCursor();
                    const token = cm.getTokenAt(cursor);
                    if (token.string && token.string.length >= 2 && /^[\w.]/.test(token.string)) {
                        cm.showHint({ hint: cibHintFunction, completeSingle: false });
                    }
                }
            });

            this.$nextTick(() => {
                if (this.cmEditor) this.cmEditor.refresh();
            });
        },

        /** Sync CodeMirror content when indicator.code changes externally */
        syncEditorCode() {
            if (this.cmEditor) {
                this.cmIgnoreChange = true;
                this.cmEditor.setValue(this.indicator.code || '');
                this.cmIgnoreChange = false;
                this.$nextTick(() => { if (this.cmEditor) this.cmEditor.refresh(); });
            }
        },

        // ── Code editing (legacy methods kept for compatibility) ──
        onCodeInput(e) {
            this.indicator.code = e.target.value;
            this.debouncedRun();
        },
        onKeydown(e) {
            // No-op: CodeMirror handles all key events
        },
        syncScroll() {
            // No-op: CodeMirror handles scrolling
        },

        // ── Inputs ──
        addInput() {
            this.indicator.options.push({
                key: 'param' + (this.indicator.options.length + 1),
                label: 'Param ' + (this.indicator.options.length + 1),
                type: 'number', default: 14, min: 1, max: 200,
            });
            this.syncDefaults();
        },
        removeInput(i) {
            const key = this.indicator.options[i].key;
            this.indicator.options.splice(i, 1);
            delete this.currentValues[key];
        },
        syncDefaults() {
            for (const opt of this.indicator.options) {
                if (this.currentValues[opt.key] === undefined) {
                    this.$set(this.currentValues, opt.key, opt.default);
                }
            }
        },
        addOutput() {
            this.indicator.plotConfig.outputs.push({
                key: 'output' + (this.indicator.plotConfig.outputs.length + 1),
                label: 'Output', color: '#2962ff', width: 1.5, style: 'line',
            });
        },

        // ── Execution ──
        debouncedRun() {
            if (this._runTimer) clearTimeout(this._runTimer);
            this._runTimer = setTimeout(() => this.runIndicator(), 600);
        },
        async runIndicator() {
            if (this.isRunning || !this.indicator.code) return;
            this.isRunning = true;
            this.errors = [];
            try {
                const input = this.getChartInput();
                const result = await this.executor.execute(this.indicator.code, input, { ...this.currentValues });
                if (result.success) {
                    this.lastResult = result.result;
                    this.lastElapsed = result.elapsed;
                    this.errors = [];
                    // Auto-update chart if already applied
                    if (this.appliedToChart) this.updateChartOverlay();
                } else {
                    this.errors = result.errors;
                    this.lastResult = null;
                }
            } catch (err) {
                this.errors = [{ message: err.message }];
            } finally {
                this.isRunning = false;
            }
        },

        /**
         * Extract OHLCV data from the chart's DataCube
         */
        getChartInput() {
            if (!this.dc || !this.dc.data || !this.dc.data.chart || !this.dc.data.chart.data) {
                // Fallback: generate sample data
                const { generateSampleData } = require('./sample-data.js');
                return generateSampleData(500);
            }
            const candles = this.dc.data.chart.data;
            const len = candles.length;
            const open = new Float64Array(len);
            const high = new Float64Array(len);
            const low = new Float64Array(len);
            const close = new Float64Array(len);
            const volume = new Float64Array(len);
            for (let i = 0; i < len; i++) {
                // DataCube format: [timestamp, open, high, low, close, volume]
                open[i] = candles[i][1];
                high[i] = candles[i][2];
                low[i] = candles[i][3];
                close[i] = candles[i][4];
                volume[i] = candles[i][5] || 0;
            }
            return { open, high, low, close, volume };
        },

        /**
         * Apply the computed indicator result to the live chart
         */
        applyToChart() {
            if (!this.lastResult || !this.dc) return;
            const priorSignalConfig=getRunner().getApplied().find(item=>item.id===this.indicator.id)?.signalConfig;
            
            // Remove previous if exists (using the original logic to avoid loops)
            if (this.appliedToChart) this.removeFromChart();

            const candles = this.dc.data.chart.data;
            const side = this.indicator.side; // 'onchart' or 'offchart'
            const arr = this.dc.data[side];

            // Check if result contains drawings
            const hasDrawings = this.lastResult && this.lastResult.__drawings;
            const dataResult = hasDrawings ? this.lastResult.__data : this.lastResult;
            const drawings = hasDrawings ? this.lastResult.__drawings : [];

            const startIdx = arr.length;
            let overlayCount = 0;

            const plotOutputs = this.indicator.plotConfig.outputs || [];

            // Apply main data result as one visible indicator. Auxiliary graphics
            // stay attached to the chart but are hidden from the legend.
            if (dataResult) {
                const outputs = this.parseOutputs(dataResult);

                if (outputs.length <= 1) {
                    const overlayData = this.buildOverlayData(outputs[0] ? outputs[0].data : dataResult, candles);
                    const overlay = {
                        name: this.indicator.name || 'CandleScript',
                        type: 'Spline',
                        data: overlayData,
                        settings: this.buildOverlaySettings(),
                    };
                    if (side === 'offchart') overlay.grid = { height: 0.2 };
                    arr.push(overlay);
                    overlayCount++;
                } else {
                    const lineOutputs = outputs.filter((_, i) => (plotOutputs[i] || {}).style !== 'histogram');
                    const histOutputs = outputs.filter((_, i) => (plotOutputs[i] || {}).style === 'histogram');
                    const visibleOutputs = lineOutputs.length ? lineOutputs : histOutputs.slice(0, 1);
                    const visibleIndexes = visibleOutputs.map(o => outputs.indexOf(o));
                    const overlay = this.buildCompositeOverlay(
                        visibleOutputs,
                        visibleIndexes.map(i => plotOutputs[i] || {}),
                        candles,
                        this.indicator.name || 'CandleScript',
                        side
                    );
                    if (overlay) {
                        arr.push(overlay);
                        overlayCount++;
                    }

                    for (let oi = 0; oi < outputs.length; oi++) {
                        if (visibleIndexes.includes(oi)) continue;
                        const o = outputs[oi];
                        const pConf = plotOutputs[oi] || {};
                        const aux = this.buildSingleOutputOverlay(o, pConf, candles, side, true);
                        if (aux) {
                            arr.push(aux);
                            overlayCount++;
                        }
                    }
                }
            }

            // Apply drawing commands as additional overlays
            for (const drawing of drawings) {
                const overlay = this.drawingToOverlay(drawing, candles, side);
                if (overlay) {
                overlay.settings = { ...(overlay.settings || {}), legend: false, group: this.indicator.name || 'CandleScript' };
                overlay.settings.candleScriptId = this.indicator.id;
                overlay.settings.candleScriptName = this.indicator.name || 'CandleScript';
                arr.push(overlay);
                overlayCount++;
            }
            }

            this.appliedOverlayId = { side, index: startIdx, count: overlayCount };
            this.dc.update_ids();
            this.appliedToChart = true;

            // Register with the persistent runner so it survives builder close
            const runner = getRunner();
            runner.register({
                id: this.indicator.id,
                name: this.indicator.name,
                code: this.indicator.code,
                side: this.indicator.side,
                currentValues: { ...this.currentValues },
                signalConfig: priorSignalConfig,
                plotConfig: JSON.parse(JSON.stringify(this.indicator.plotConfig)),
            });
            const signal=customSignalData(dataResult,candles,this.indicator.plotConfig);
            const signalConfig=runner.getApplied().find(item=>item.id===this.indicator.id)?.signalConfig;
            for(const overlay of arr)if(overlay.settings?.candleScriptId===this.indicator.id && overlay.settings.legend!==false){
                overlay.signalData=signal.data;overlay.settings.signalOutputs=signal.outputs;
                if(signalConfig)overlay.settings.signalConfig=signalConfig;
            }
        },

        /**
         * Convert a drawing command to a DataCube overlay
         */
        drawingToOverlay(drawing, candles, side) {
            switch (drawing.type) {
                case 'line': {
                    const data = [];
                    for (let i = 0; i < drawing.data.length && i < candles.length; i++) {
                        const val = drawing.data[i];
                        if (val !== undefined && val !== null && !isNaN(val) && isFinite(val)) {
                            data.push([candles[i][0], val]);
                        }
                    }
                    return {
                        name: drawing.title || 'Line',
                        type: 'Spline',
                        data: data,
                        settings: {
                            color: drawing.color || '#2962ff',
                            lineWidth: drawing.width || 1.5,
                            lineStyle: drawing.style || 'solid',
                        },
                    };
                }
                case 'histogram': {
                    const data = [];
                    for (let i = 0; i < drawing.data.length && i < candles.length; i++) {
                        const val = drawing.data[i];
                        if (val !== undefined && val !== null && !isNaN(val)) {
                            data.push([candles[i][0], val]);
                        }
                    }
                    return {
                        name: drawing.title || 'Histogram',
                        type: 'Histogram',
                        data: data,
                        settings: {
                            color: drawing.color || '#26a69a',
                            colorUp: drawing.colorUp || '#26a69a',
                            colorDown: drawing.colorDown || '#ef5350',
                        },
                    };
                }
                case 'arrow':
                case 'shape': {
                    // Convert to Spline with markers
                    const data = [];
                    for (let i = 0; i < drawing.data.length && i < candles.length; i++) {
                        const val = drawing.data[i];
                        if (val !== undefined && val !== null && !isNaN(val) && isFinite(val)) {
                            data.push([candles[i][0], val]);
                        }
                    }
                    return {
                        name: drawing.title || drawing.text || (drawing.type === 'arrow' ? 'Signal' : 'Shape'),
                        type: 'Spline',
                        data: data,
                        settings: {
                            color: drawing.color || '#2962ff',
                            lineWidth: 0, // no line, just markers
                            markerSize: (drawing.size || 1) * 6,
                            markerColor: drawing.color || '#2962ff',
                            markerShape: drawing.shape || drawing.direction || 'circle',
                        },
                    };
                }
                case 'hline': {
                    // Horizontal line — create a constant-value line across all bars
                    const data = candles.map(c => [c[0], drawing.price]);
                    return {
                        name: drawing.title || `Level ${drawing.price}`,
                        type: 'Spline',
                        data: data,
                        settings: {
                            color: drawing.color || '#787b86',
                            lineWidth: drawing.width || 1,
                            lineStyle: drawing.style || 'dashed',
                        },
                    };
                }
                case 'candle': {
                    // Custom candles — build OHLC overlay data
                    const data = [];
                    const len = Math.min(drawing.open.length, candles.length);
                    for (let i = 0; i < len; i++) {
                        data.push([candles[i][0], drawing.open[i], drawing.high[i], drawing.low[i], drawing.close[i]]);
                    }
                    return {
                        name: drawing.title || 'Custom Candles',
                        type: 'Candles',
                        data: data,
                        settings: {
                            colorUp: drawing.colorUp || '#26a69a',
                            colorDown: drawing.colorDown || '#ef5350',
                        },
                    };
                }
                case 'fill': {
                    // Fill between two lines — render as two lines (fill handled by chart if supported)
                    // For now, just add both lines
                    return null; // Fill is visual-only, handled by the chart renderer
                }
                default:
                    return null;
            }
        },

        buildCompositeOverlay(outputs, outputConfigs, candles, name, side) {
            if (!outputs.length) return null;
            if (outputs.length === 1) {
                return this.buildSingleOutputOverlay(outputs[0], outputConfigs[0] || {}, candles, side, false, name);
            }
            const data = [];
            for (let i = 0; i < candles.length; i++) {
                const row = [candles[i][0]];
                for (const output of outputs) {
                    row.push(this.valueAt(output.data, i));
                }
                data.push(row);
            }
            const overlay = {
                name,
                type: 'Splines',
                data,
                settings: {
                    colors: outputConfigs.map(conf => conf.color || '#2962ff'),
                    lineWidths: outputConfigs.map(conf => conf.width || 1.5),
                    skipNaN: true,
                    group: this.indicator.name || 'CandleScript',
                    candleScriptId: this.indicator.id,
                    candleScriptName: this.indicator.name || 'CandleScript',
                },
            };
            if (side === 'offchart') overlay.grid = { height: 0.2 };
            return overlay;
        },

        buildSingleOutputOverlay(output, config, candles, side, hidden = false, nameOverride = null) {
            const data = this.buildOverlayData(output.data, candles);
            if (!data.length) return null;
            const overlay = {
                name: nameOverride || `${this.indicator.name || 'CandleScript'} — ${output.key || 'Aux'}`,
                type: config.style === 'histogram' ? 'Histogram' : 'Spline',
                data,
                settings: {
                    color: config.color || '#2962ff',
                    lineWidth: config.width || 1.5,
                    legend: hidden ? false : undefined,
                    group: this.indicator.name || 'CandleScript',
                    candleScriptId: this.indicator.id,
                    candleScriptName: this.indicator.name || 'CandleScript',
                },
            };
            if (side === 'offchart') overlay.grid = { height: 0.2 };
            return overlay;
        },

        valueAt(series, index) {
            const value = series && series[index];
            if (value && typeof value === 'object' && value.value !== undefined) return value.value;
            if (value === undefined || value === null || isNaN(value) || !isFinite(value)) return null;
            return value;
        },

        /**
         * Update the already-applied overlay with new data
         */
        updateChartOverlay() {
            if (!this.appliedOverlayId || !this.dc || !this.lastResult) return;
            // Re-apply from scratch for simplicity when drawings are involved
            this.removeFromChart();
            this.applyToChart();
        },

        /**
         * Remove any existing overlays with the same indicator name
         */
        removeExistingOverlays() {
            if (!this.dc || !this.dc.data) return;
            const name = this.indicator.name || 'CandleScript';
            const sides = ['onchart', 'offchart'];
            
            for (const side of sides) {
                const arr = this.dc.data[side];
                if (!arr) continue;
                
                // Remove from end to start to preserve indices
                for (let i = arr.length - 1; i >= 0; i--) {
                    const ov = arr[i];
                    if (ov && ov.name && (
                        ov.name === name || 
                        ov.name.startsWith(name + ' —') ||
                        ov.name.startsWith(name + ' -')
                    )) {
                        arr.splice(i, 1);
                    }
                }
            }
            
            this.dc.update_ids();
        },

        /**
         * Remove the applied overlay from the chart
         */
        removeFromChart() {
            if (!this.dc) return;

            const runner = getRunner();
            runner.removeFromChart(this.dc, this.indicator.id, this.indicator.name);
            this.$emit('remove-candlescript-indicator', {
                id: this.indicator.id,
                name: this.indicator.name,
            });

            this.appliedToChart = false;
            this.appliedOverlayId = null;
        },

        /**
         * Remove stale empty-data overlays that were restored by _savedDrawings
         * after a timeframe switch. These are shells with matching indicator name
         * but empty data arrays.
         */
        removeStaleOverlays(dc) {
            if (!dc || !dc.data) return;
            const name = this.indicator.name || 'CandleScript';
            const sides = ['onchart', 'offchart'];
            for (const side of sides) {
                const arr = dc.data[side];
                if (!arr) continue;
                // Remove from end to start to preserve indices
                for (let i = arr.length - 1; i >= 0; i--) {
                    const ov = arr[i];
                    if (ov && ov.name && (
                        ov.name.startsWith(name) ||
                        ov.name.startsWith(name + ' —') ||
                        ov.name.startsWith(name + ' -')
                    ) && (!ov.data || ov.data.length === 0)) {
                        arr.splice(i, 1);
                    }
                }
            }
            dc.update_ids();
        },

        /**
         * Convert indicator result to DataCube overlay data format
         * Format: [[timestamp, value], [timestamp, value], ...]
         */
        buildOverlayData(result, candles) {
            const data = [];
            // Unwrap if it's a drawing-enhanced result
            const actualResult = (result && result.__data) ? result.__data : result;

            if (Array.isArray(actualResult) || ArrayBuffer.isView(actualResult)) {
                for (let i = 0; i < actualResult.length && i < candles.length; i++) {
                    const ts = candles[i][0];
                    const val = typeof actualResult[i] === 'object' && actualResult[i] !== null
                        ? actualResult[i].value
                        : actualResult[i];
                    if (val !== undefined && val !== null && !isNaN(val) && isFinite(val)) {
                        data.push([ts, val]);
                    }
                }
            } else if (typeof actualResult === 'number') {
                // Single number — shouldn't happen but handle gracefully
            }
            return data;
        },

        /**
         * Parse result into array of { key, data } outputs
         */
        parseOutputs(result) {
            if (!result) return [];
            // Unwrap drawing-enhanced result
            const actualResult = (result && result.__data) ? result.__data : result;

            if (Array.isArray(actualResult) || ArrayBuffer.isView(actualResult)) {
                if (actualResult.length && typeof actualResult[0] === 'object' && actualResult[0] !== null && 'value' in actualResult[0]) {
                    return [{ key: 'value', data: actualResult }];
                }
                return [{ key: 'value', data: actualResult }];
            }
            if (typeof actualResult === 'object' && actualResult !== null) {
                const outputs = [];
                const configKeys = (this.indicator.plotConfig.outputs || []).map(o => o.key);
                const keys = configKeys.length
                    ? configKeys.filter(k => actualResult[k] !== undefined)
                    : Object.keys(actualResult).filter(k => Array.isArray(actualResult[k]) || ArrayBuffer.isView(actualResult[k]));
                for (const key of keys) {
                    outputs.push({ key, data: actualResult[key] });
                }
                return outputs;
            }
            return [];
        },

        buildOverlaySettings() {
            const out = (this.indicator.plotConfig.outputs || [])[0] || {};
            return {
                color: out.color || '#2962ff',
                lineWidth: out.width || 1.5,
                group: this.indicator.name || 'CandleScript',
                candleScriptId: this.indicator.id,
                candleScriptName: this.indicator.name || 'CandleScript',
            };
        },

        // ── Templates & Save/Load ──
        loadTemplate(tmpl) {
            this.indicator = createIndicatorConfig({
                name: tmpl.name,
                code: tmpl.code,
                options: JSON.parse(JSON.stringify(tmpl.options)),
                plotConfig: JSON.parse(JSON.stringify(tmpl.plotConfig)),
                side: tmpl.side,
                category: tmpl.category,
            });
            this.currentValues = getDefaultValues(tmpl.options);
            this.showTemplates = false;
            this.lastResult = null;
            this.appliedToChart = false;
            this.appliedOverlayId = null;
            this.errors = [];
            this.syncEditorCode();
            this.$nextTick(() => this.runIndicator());
        },
        saveIndicatorConfig() {
            this.indicator.currentValues = { ...this.currentValues };
            saveIndicator(this.indicator);
            this.savedList = loadAllIndicators();
        },
        saveIndicator() {
            this.saveIndicatorConfig();
        },
        loadSaved(ind) {
            this.indicator = JSON.parse(JSON.stringify(ind));
            this.currentValues = ind.currentValues || getDefaultValues(ind.options);
            this.showSaved = false;
            this.lastResult = null;
            this.appliedToChart = false;
            this.appliedOverlayId = null;
            this.errors = [];
            this.syncEditorCode();
            this.$nextTick(() => this.runIndicator());
        },
        deleteSaved(id) {
            const indicator = this.savedList.find(ind => ind.id === id);
            deleteIndicator(id);
            if (indicator) {
                const runner = getRunner();
                runner.removeFromChart(this.dc, indicator.id, indicator.name);
                this.$emit('remove-candlescript-indicator', {
                    id: indicator.id,
                    name: indicator.name,
                });
                if (this.indicator.id === indicator.id) {
                    this.appliedToChart = false;
                    this.appliedOverlayId = null;
                }
            }
            this.savedList = loadAllIndicators();
        },

        // ── AI Generation ──
        toggleAiPanel() {
            // Check if AI is enabled in settings
            if (!this.aiSettingsEnabled) {
                this.$emit('error', 'AI indicator generation is disabled. Enable it in Dashboard > AI Settings.');
                return;
            }
            
            this.showAiPanel = !this.showAiPanel;
            if (this.showAiPanel) {
                this.loadAiCredits();
                this.$nextTick(() => {
                    if (this.$refs.aiPrompt) this.$refs.aiPrompt.focus();
                });
            }
        },
        async loadAiCredits() {
            const uid = this.userId || (this.$clerk && this.$clerk.user ? this.$clerk.user.id : null);
            if (!uid) return;
            const credits = await aiIndicatorService.getCredits(uid);
            if (credits && credits.remaining !== undefined) {
                this.aiCredits = credits.remaining;
            } else if (credits && credits.credits !== undefined) {
                this.aiCredits = credits.credits;
            }
        },
        async generateFromAI() {
            if (this.aiGenerating || !this.aiPromptText.trim()) return;
            
            // Check if AI is enabled in settings
            if (!this.aiSettingsEnabled) {
                this.aiError = 'AI indicator generation is disabled. Enable it in Dashboard > AI Settings.';
                return;
            }
            
            this.aiGenerating = true;
            this.aiError = '';
            this.aiDescription = '';

            const uid = this.userId || (this.$clerk && this.$clerk.user ? this.$clerk.user.id : null);

            try {
                const result = await aiIndicatorService.generateIndicator(this.aiPromptText.trim(), uid);

                // Apply the generated indicator config
                this.indicator = createIndicatorConfig({
                    name: result.name,
                    code: result.code,
                    options: result.options || [],
                    plotConfig: result.plotConfig || { outputs: [{ key: 'value', label: 'Output', color: '#2962ff', width: 1.5, style: 'line' }] },
                    side: result.side || 'offchart',
                });
                this.currentValues = getDefaultValues(result.options || []);
                this.aiDescription = result.description || '';

                // Reset state
                this.lastResult = null;
                this.appliedToChart = false;
                this.appliedOverlayId = null;
                this.errors = [];
                this.tab = 'code';
                this.showAiPanel = false;
                this.syncEditorCode();

                // Auto-run the generated indicator
                this.$nextTick(() => this.runIndicator());

                // Refresh credits
                this.loadAiCredits();
            } catch (error) {
                this.aiError = error.message || 'Failed to generate indicator. Please try again.';
            } finally {
                this.aiGenerating = false;
            }
        },
    },
};
</script>

<style>
/* CodeMirror base styles for ChartIndicatorBuilder */
@import '~codemirror/lib/codemirror.css';
@import '~codemirror/addon/hint/show-hint.css';
@import '~codemirror/theme/dracula.css';
</style>

<style scoped>
.cib-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 13px;
    color: var(--tc-text-primary, #131722);
    position: relative;
}

/* Header */
.cib-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-bottom: 1px solid var(--tc-border-primary, #e0e3eb);
    flex-shrink: 0;
}
.cib-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--tc-accent, #2962ff);
    font-weight: 600;
    font-size: 14px;
}
.cib-header-right { display: flex; gap: 4px; }
.cib-icon-btn {
    background: none; border: none; color: var(--tc-text-secondary, #787b86);
    cursor: pointer; padding: 5px; border-radius: 5px; display: flex; align-items: center;
}
.cib-icon-btn:hover { background: var(--tc-bg-hover, rgba(0,0,0,0.04)); color: var(--tc-text-primary); }

/* Dropdowns */
.cib-dropdown {
    position: absolute; top: 44px; left: 8px; right: 8px; z-index: 50;
    background: var(--tc-bg-elevated, #fff); border: 1px solid var(--tc-border-primary, #e0e3eb);
    border-radius: 8px; box-shadow: var(--tc-shadow-md); max-height: 260px; overflow-y: auto; padding: 4px;
}
.cib-dropdown-title { font-size: 11px; font-weight: 600; color: var(--tc-text-tertiary); padding: 6px 10px; text-transform: uppercase; letter-spacing: 0.5px; }
.cib-dropdown-item {
    display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 6px; cursor: pointer; font-size: 13px;
}
.cib-dropdown-item:hover { background: var(--tc-accent-bg, rgba(41,98,255,0.08)); }
.cib-dropdown-empty { padding: 16px; text-align: center; color: var(--tc-text-tertiary); font-size: 12px; }
.cib-dropdown-del { background: none; border: none; color: var(--tc-text-tertiary); cursor: pointer; margin-left: auto; font-size: 16px; line-height: 1; }
.cib-dropdown-del:hover { color: var(--tc-red, #ef4444); }
.cib-tmpl-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.cib-cat-trend { background: #2962ff; }
.cib-cat-oscillator { background: #7e57c2; }
.cib-cat-volatility { background: #ff6d00; }
.cib-cat-custom { background: #26a69a; }
.cib-tmpl-side { margin-left: auto; font-size: 10px; color: var(--tc-text-tertiary); }

/* Meta row */
.cib-meta-row {
    display: flex; gap: 6px; padding: 8px 12px; border-bottom: 1px solid var(--tc-border-secondary, #f0f3fa); flex-shrink: 0;
}
.cib-name-input {
    flex: 1; background: var(--tc-input-bg, #fff); border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 5px; padding: 5px 8px; font-size: 13px; font-weight: 500; color: var(--tc-text-primary); outline: none;
}
.cib-name-input:focus { border-color: var(--tc-accent); }
.cib-side-select {
    background: var(--tc-input-bg); border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 5px; padding: 5px 6px; font-size: 12px; color: var(--tc-text-primary); outline: none; cursor: pointer;
}

/* Tabs */
.cib-tabs {
    display: flex; border-bottom: 1px solid var(--tc-border-primary, #e0e3eb); flex-shrink: 0;
}
.cib-tab {
    flex: 1; padding: 8px 0; background: none; border: none; border-bottom: 2px solid transparent;
    font-size: 12px; font-weight: 500; color: var(--tc-text-secondary); cursor: pointer;
}
.cib-tab:hover { color: var(--tc-text-primary); }
.cib-tab.active { color: var(--tc-accent, #2962ff); border-bottom-color: var(--tc-accent); }

/* Tab body */
.cib-tab-body { flex: 1; overflow-y: auto; overflow-x: hidden; }

/* Code tab */
.cib-code-tab { position: relative; display: flex; flex-direction: column; }
.cib-editor-wrap { flex: 1; overflow: hidden; position: relative; }

/* CodeMirror overrides for inline builder */
.cib-editor-wrap .CodeMirror {
    height: 100%;
    font-family: 'SF Mono', 'Fira Code', 'JetBrains Mono', 'Consolas', monospace;
    font-size: 12px;
    line-height: 1.5;
}
.cib-editor-wrap .CodeMirror-gutters {
    border-right: 1px solid var(--tc-border-secondary, #f0f3fa);
    background: var(--tc-bg-secondary, #f8f9fd);
}
.cib-editor-wrap .CodeMirror-linenumber {
    font-size: 10px;
    color: var(--tc-text-tertiary, #9ca3af);
    padding: 0 6px 0 4px;
}

/* Light theme token colors */
.cib-editor-wrap .cm-keyword { color: #d73a49; font-weight: 500; }
.cib-editor-wrap .cm-def { color: #6f42c1; }
.cib-editor-wrap .cm-variable { color: #24292e; }
.cib-editor-wrap .cm-variable-2 { color: #005cc5; }
.cib-editor-wrap .cm-property { color: #005cc5; }
.cib-editor-wrap .cm-operator { color: #d73a49; }
.cib-editor-wrap .cm-number { color: #005cc5; }
.cib-editor-wrap .cm-string { color: #032f62; }
.cib-editor-wrap .cm-comment { color: #6a737d; font-style: italic; }
.cib-editor-wrap .cm-atom { color: #005cc5; }
.cib-editor-wrap .cm-builtin { color: #6f42c1; }

/* Inputs tab */
.cib-inputs-tab { padding: 8px; }
.cib-empty { padding: 16px; text-align: center; color: var(--tc-text-tertiary); font-size: 12px; }
.cib-empty code { background: var(--tc-bg-tertiary, #f0f3fa); padding: 1px 4px; border-radius: 3px; font-size: 11px; }
.cib-input-row { border: 1px solid var(--tc-border-primary, #e0e3eb); border-radius: 6px; margin-bottom: 8px; overflow: hidden; }
.cib-input-header { display: flex; align-items: center; gap: 4px; padding: 6px 8px; background: var(--tc-bg-secondary, #f8f9fd); border-bottom: 1px solid var(--tc-border-secondary); }
.cib-input-key { flex: 1; font-family: monospace; font-size: 12px; padding: 3px 6px; border: 1px solid var(--tc-input-border); border-radius: 3px; background: var(--tc-input-bg); color: var(--tc-text-primary); outline: none; }
.cib-input-type { font-size: 11px; padding: 3px 4px; border: 1px solid var(--tc-input-border); border-radius: 3px; background: var(--tc-input-bg); color: var(--tc-text-primary); outline: none; cursor: pointer; }
.cib-input-del { background: none; border: none; color: var(--tc-text-tertiary); cursor: pointer; font-size: 14px; padding: 2px 4px; }
.cib-input-del:hover { color: var(--tc-red, #ef4444); }
.cib-input-body { padding: 8px; }
.cib-input-num-row { display: flex; align-items: center; gap: 4px; margin-bottom: 4px; }
.cib-input-num-row label { font-size: 10px; color: var(--tc-text-tertiary); }
.cib-input-num-row input { width: 52px; font-size: 12px; padding: 3px 4px; border: 1px solid var(--tc-input-border); border-radius: 3px; background: var(--tc-input-bg); color: var(--tc-text-primary); outline: none; }
.cib-range { width: 100%; height: 3px; -webkit-appearance: none; appearance: none; background: var(--tc-border-primary); border-radius: 2px; outline: none; margin-top: 4px; }
.cib-range::-webkit-slider-thumb { -webkit-appearance: none; width: 12px; height: 12px; border-radius: 50%; background: var(--tc-accent, #2962ff); cursor: pointer; }
.cib-choices-input { width: 100%; font-size: 12px; padding: 4px 6px; border: 1px solid var(--tc-input-border); border-radius: 3px; background: var(--tc-input-bg); color: var(--tc-text-primary); outline: none; margin-bottom: 4px; }
.cib-toggle { position: relative; display: inline-block; width: 32px; height: 18px; }
.cib-toggle input { opacity: 0; width: 0; height: 0; }
.cib-toggle-track { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background: var(--tc-border-primary); border-radius: 9px; transition: 0.2s; }
.cib-toggle-track:before { content: ""; position: absolute; height: 14px; width: 14px; left: 2px; bottom: 2px; background: white; border-radius: 50%; transition: 0.2s; }
.cib-toggle input:checked + .cib-toggle-track { background: var(--tc-accent); }
.cib-toggle input:checked + .cib-toggle-track:before { transform: translateX(14px); }
.cib-add-input-btn { width: 100%; padding: 8px; background: none; border: 1px dashed var(--tc-border-primary); border-radius: 6px; color: var(--tc-text-secondary); cursor: pointer; font-size: 12px; }
.cib-add-input-btn:hover { border-color: var(--tc-accent); color: var(--tc-accent); }

/* Style tab */
.cib-style-tab { padding: 8px; }
.cib-style-row { display: flex; align-items: center; gap: 4px; margin-bottom: 6px; }
.cib-style-key { width: 60px; font-family: monospace; font-size: 11px; padding: 3px 4px; border: 1px solid var(--tc-input-border); border-radius: 3px; background: var(--tc-input-bg); color: var(--tc-text-primary); outline: none; }
.cib-style-label { flex: 1; font-size: 12px; padding: 3px 4px; border: 1px solid var(--tc-input-border); border-radius: 3px; background: var(--tc-input-bg); color: var(--tc-text-primary); outline: none; }
.cib-color-pick { width: 24px; height: 24px; border: 1px solid var(--tc-input-border); border-radius: 3px; padding: 1px; cursor: pointer; }
.cib-style-sel { font-size: 11px; padding: 3px 4px; border: 1px solid var(--tc-input-border); border-radius: 3px; background: var(--tc-input-bg); color: var(--tc-text-primary); outline: none; cursor: pointer; }

/* Footer */
.cib-footer {
    display: flex; align-items: center; justify-content: space-between;
    padding: 8px 12px; border-top: 1px solid var(--tc-border-primary, #e0e3eb); flex-shrink: 0;
    background: var(--tc-bg-secondary, #f8f9fd);
}
.cib-status { display: flex; align-items: center; gap: 8px; }
.cib-status-err { font-size: 11px; color: var(--tc-red, #ef4444); font-weight: 500; }
.cib-status-ok { font-size: 11px; color: var(--tc-green, #22c55e); font-weight: 500; }
.cib-status-applied { font-size: 10px; background: var(--tc-green-bg, rgba(34,197,94,0.1)); color: var(--tc-green); padding: 1px 6px; border-radius: 3px; font-weight: 600; }
.cib-actions { display: flex; gap: 4px; }
.cib-btn {
    display: inline-flex; align-items: center; gap: 4px; padding: 5px 10px; border: none; border-radius: 5px;
    font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.15s;
}
.cib-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.cib-btn-save { background: var(--tc-bg-hover, rgba(0,0,0,0.04)); color: var(--tc-text-secondary); }
.cib-btn-save:hover { background: var(--tc-bg-active); }
.cib-btn-run { background: var(--tc-accent, #2962ff); color: #fff; }
.cib-btn-run:hover:not(:disabled) { background: var(--tc-accent-hover, #1e50d8); }
.cib-btn-apply { background: var(--tc-green, #22c55e); color: #fff; }
.cib-btn-apply:hover:not(:disabled) { background: var(--tc-green-hover, #16a34a); }
.cib-btn-remove { background: var(--tc-red-bg, rgba(239,68,68,0.1)); color: var(--tc-red, #ef4444); }
.cib-btn-remove:hover { background: rgba(239,68,68,0.2); }
.cib-spinner { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: cib-spin 0.6s linear infinite; }
@keyframes cib-spin { to { transform: rotate(360deg); } }

/* Errors */
.cib-errors {
    border-top: 1px solid var(--tc-red, #ef4444); background: var(--tc-red-bg, rgba(239,68,68,0.05));
    padding: 6px 12px; max-height: 80px; overflow-y: auto; flex-shrink: 0;
}
.cib-error-line { font-size: 11px; color: var(--tc-red); font-family: monospace; padding: 1px 0; }

/* AI Panel */
.cib-ai-btn.active { color: var(--tc-accent, #2962ff); background: var(--tc-accent-bg, rgba(41,98,255,0.1)); }
.cib-ai-btn.disabled { 
    color: var(--tc-text-tertiary, #9ca3af); 
    opacity: 0.5; 
    cursor: not-allowed; 
}
.cib-ai-btn.disabled:hover { 
    background: none; 
    color: var(--tc-text-tertiary, #9ca3af); 
}
.cib-ai-panel {
    border-bottom: 1px solid var(--tc-border-primary, #e0e3eb);
    background: var(--tc-bg-secondary, #f8f9fd);
    flex-shrink: 0;
}
.cib-ai-header {
    display: flex; align-items: center; gap: 6px; padding: 8px 12px;
    font-size: 12px; font-weight: 600; color: var(--tc-accent, #2962ff);
    border-bottom: 1px solid var(--tc-border-secondary, #f0f3fa);
}
.cib-ai-credits {
    margin-left: auto; font-size: 10px; font-weight: 600;
    background: var(--tc-accent-bg, rgba(41,98,255,0.1)); color: var(--tc-accent);
    padding: 2px 8px; border-radius: 10px;
}
.cib-ai-credits.low {
    background: var(--tc-red-bg, rgba(239,68,68,0.1)); color: var(--tc-red, #ef4444);
}
.cib-ai-body { padding: 8px 12px; }
.cib-ai-prompt {
    width: 100%; min-height: 60px; max-height: 100px; padding: 8px 10px;
    font-size: 12px; font-family: inherit; color: var(--tc-text-primary);
    background: var(--tc-input-bg, #fff); border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 6px; outline: none; resize: vertical; line-height: 1.5;
    box-sizing: border-box;
}
.cib-ai-prompt:focus { border-color: var(--tc-accent, #2962ff); }
.cib-ai-prompt::placeholder { color: var(--tc-text-tertiary, #9ca3af); font-size: 11px; }

/* AI Example Chips */
.cib-ai-examples { margin-top: 8px; }
.cib-ai-examples-title {
    font-size: 10px; font-weight: 600; color: var(--tc-text-tertiary, #9ca3af);
    text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;
}
.cib-ai-examples-grid {
    display: flex; flex-wrap: wrap; gap: 5px;
}
.cib-ai-example-chip {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 10px 4px 6px; border-radius: 16px;
    background: var(--tc-bg-tertiary, #f0f3fa);
    border: 1px solid var(--tc-border-secondary, #e8ebf2);
    font-size: 11px; color: var(--tc-text-secondary, #4b5563);
    cursor: pointer; transition: all 0.15s; white-space: nowrap;
}
.cib-ai-example-chip:hover {
    background: var(--tc-accent-bg, rgba(41,98,255,0.08));
    border-color: var(--tc-accent, #2962ff);
    color: var(--tc-accent, #2962ff);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(41,98,255,0.1);
}
.cib-ai-chip-icon { font-size: 12px; line-height: 1; }
.cib-ai-chip-text { font-weight: 500; }
.cib-ai-footer {
    display: flex; align-items: center; justify-content: space-between; margin-top: 8px;
}
.cib-ai-hint { font-size: 10px; color: var(--tc-text-tertiary, #9ca3af); }
.cib-btn-ai {
    background: linear-gradient(135deg, #2962ff, #7c3aed); color: #fff;
    border: none; border-radius: 5px; padding: 6px 12px; font-size: 12px; font-weight: 500;
    cursor: pointer; display: inline-flex; align-items: center; gap: 4px; transition: all 0.15s;
}
.cib-btn-ai:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.cib-btn-ai:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.cib-ai-error {
    margin-top: 6px; padding: 6px 8px; background: var(--tc-red-bg, rgba(239,68,68,0.08));
    border-radius: 4px; font-size: 11px; color: var(--tc-red, #ef4444);
}
.cib-ai-desc {
    margin-top: 6px; padding: 6px 8px; background: var(--tc-green-bg, rgba(34,197,94,0.08));
    border-radius: 4px; font-size: 11px; color: var(--tc-green, #22c55e); font-style: italic;
}
</style>

<style>
/* CodeMirror hint styles for ChartIndicatorBuilder (unscoped for popup portal) */
.cib-hint-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0;
}
.cib-hint-icon {
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
}
.cib-hint-icon.cib-hint-function { background: #dbeafe; color: #2563eb; }
.cib-hint-icon.cib-hint-property { background: #fef3c7; color: #d97706; }
.cib-hint-icon.cib-hint-class { background: #ede9fe; color: #7c3aed; }
.cib-hint-name {
    font-weight: 500;
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 11px;
}
.cib-hint-desc {
    font-size: 10px;
    color: #9ca3af;
    margin-left: auto;
    padding-left: 8px;
}
</style>
